/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo, useEffect } from 'react';
import PointsOfSaleContext from './PointsOfSaleContext';
import initialPointsOfSaleState from './initialPointsOfSaleState';
import pointsOfSaleReducer from './reducer/pointsOfSale.reducer';
import * as action from './reducer/pointsOfSale.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import {
  IcreatePointOfSaleActionProps,
  IremovePointOfSaleActionProps,
  IselectPointOfSaleActionProps,
  IupdatePointOfSaleActionProps,
} from 'interfaces/pointsOfSale.interface';
import { ChildrenProps } from 'interfaces/general.interface';
import useContextGeneral from 'hooks/contexts/useContextGeneral';
import useContextCompany from 'hooks/contexts/useContextCompany';

function PointsOfSaleProvider(props: ChildrenProps) {
  const [pointsOfSaleState, dispatch] = useReducer(
    pointsOfSaleReducer,
    initialPointsOfSaleState
  );
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { t: translate } = useTranslation();
  const { company } = useContextCompany();
  const { countries } = useContextGeneral();

  useEffect(() => {
    if (!company?.id || !isAuthenticated) return;
    (async () => {
      const token = await getAccessTokenSilently();
      return await action.getPointsOfSaleAction({
        dispatch,
        clientId: company.id,
        token,
        translate,
      });
    })();
  }, [company?.id, isAuthenticated]);

  const selectPointOfSale = useCallback(
    ({ pointOfSale }: IselectPointOfSaleActionProps) => {
      return action.selectPointOfSaleAction({ dispatch, pointOfSale });
    },
    []
  );

  const createPointOfSale = useCallback(
    async ({ pointOfSale }: { pointOfSale: IcreatePointOfSaleActionProps }) => {
      if (!company?.id || countries.length === 0) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      pointOfSale.client_id = company.id;
      return await action.createPointOfSaleAction({
        dispatch,
        pointOfSale,
        countries,
        token,
        translate,
      });
    },
    [company?.id, countries]
  );

  const updatePointOfSale = useCallback(
    async ({ pointOfSale }: { pointOfSale: IupdatePointOfSaleActionProps }) => {
      if (countries.length === 0) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updatePointOfSaleAction({
        dispatch,
        pointOfSale,
        countries,
        token,
        translate,
      });
    },
    [countries]
  );

  const removePointOfSale = useCallback(
    async ({ pointOfSaleId }: IremovePointOfSaleActionProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.removePointOfSaleAction({
        dispatch,
        pointOfSaleId,
        token,
        translate,
      });
    },
    []
  );

  const memoProvider = useMemo(
    () => ({
      ...pointsOfSaleState,
      selectPointOfSale,
      createPointOfSale,
      updatePointOfSale,
      removePointOfSale,
    }),
    [
      pointsOfSaleState,
      selectPointOfSale,
      createPointOfSale,
      updatePointOfSale,
      removePointOfSale,
    ]
  );

  return (
    <PointsOfSaleContext.Provider value={memoProvider}>
      {props.children}
    </PointsOfSaleContext.Provider>
  );
}

export default memo(PointsOfSaleProvider);
